"use client";
import React, { useState } from "react";
import Image from "next/image";
import { teamMembers } from "./team-data";

export default function TeamSection() {
  const [activeId, setActiveId] = useState<number>(1); // Default to Vipin Jain

  const handleImageClick = (id: number) => {
    setActiveId(id);
  };

  const handleTitleClick = (id: number) => {
    setActiveId(id);
  };

  return (
    <section className="bg-black py-[80px] lg:py-[120px]">
      <div className="max-w-[2500px] mx-auto px-4 w-[calc(100vw_-_10%)]">
        <div className="flex flex-col gap-8 items-center justify-between pb-[80px] mb-[80px] border-b border-white/10">
          <div className=" flex gap-10">
            <p className="text-white text-[16px] uppercase mt-2 font-medium">
              Teams
            </p>
            <div className="flex flex-col">
              <h2 className="text-[length:var(--section-title)] leading-[120%] text-white clash-font font-medium">
                Meet Our Team of Lateral Thinkers. We Rise by Lifting
                <span className="text-[#225BD4]">
                  {" "}
                  Our Fellows, Our Partners{" "}
                </span>
                and <span className="text-[#225BD4]">Our Clients.</span>
              </h2>
              <p className="text-[#949596] text-[clamp(0.9375rem,0.4664rem+0.9815vw,2rem)] leading-[140%]">
                Our core leadership team inspires innovation to drive impact
                across diverse industries. By leading a team of top-tier
                engineers and technologists, our leaders have delivered complex
                enterprise-grade digital transformation initiatives, crafted
                AI-native solutions adopted by global businesses, and developed
                scalable digital solutions that have revolutionized industry
                benchmarks.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-[50px] lg:gap-[100px] items-center">
          {/* Left side - Images Grid */}
          <div className="w-full lg:w-[50%] relative">
            <div className="grid grid-cols-2 gap-[15px] lg:gap-[30px]">
              {/* Image 1 - Top Left */}
              <div
                className={`relative w-full aspect-[415/500] cursor-pointer transition-opacity duration-300 group ${activeId !== 1 ? "opacity-10" : "opacity-100"}`}
                onClick={() => handleImageClick(1)}
              >
                <Image
                  src={teamMembers[0].image}
                  alt={teamMembers[0].name}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                />
                {/* Corner decorations */}
                <div className="absolute top-0 left-0 w-[14px] h-[14px]">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M0 0V14M0 0H14"
                      stroke={activeId === 1 ? "#225BD4" : "white"}
                    />
                  </svg>
                </div>
                <div className="absolute bottom-0 left-0 w-[14px] h-[14px]">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M0 14V0M0 14H14"
                      stroke={activeId === 1 ? "#225BD4" : "white"}
                    />
                  </svg>
                </div>
                <div className="absolute top-0 right-0 w-[14px] h-[14px]">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M14 0V14M14 0H0"
                      stroke={activeId === 1 ? "#225BD4" : "white"}
                    />
                  </svg>
                </div>
                <div className="absolute bottom-0 right-0 w-[14px] h-[14px]">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M14 14V0M14 14H0"
                      stroke={activeId === 1 ? "#225BD4" : "white"}
                    />
                  </svg>
                </div>
                {/* LinkedIn Button */}
                <div className="absolute top-[15px] right-[15px]">
                  <a
                    href={teamMembers[0].linkedIn}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-[40px] h-[40px] bg-[#225BD4] rounded-[5px] flex items-center justify-center hover:bg-[#1a4aa8] transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="white"
                    >
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                    </svg>
                  </a>
                </div>
              </div>

              {/* Image 2 - Top Right (offset down) */}
              <div
                className={`relative w-full aspect-[415/500] cursor-pointer transition-opacity duration-300 group mt-[30px] lg:mt-[61px] ${activeId !== 2 ? "opacity-10" : "opacity-100"}`}
                onClick={() => handleImageClick(2)}
              >
                <Image
                  src={teamMembers[1].image}
                  alt={teamMembers[1].name}
                  fill
                  className="object-cover"
                />
                {/* Corner decorations */}
                <div className="absolute top-0 left-0 w-[14px] h-[14px]">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M0 0V14M0 0H14" stroke="#225BD4" />
                  </svg>
                </div>
                <div className="absolute bottom-0 left-0 w-[14px] h-[14px]">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M0 14V0M0 14H14" stroke="#225BD4" />
                  </svg>
                </div>
                <div className="absolute top-0 right-0 w-[14px] h-[14px]">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M14 0V14M14 0H0" stroke="#225BD4" />
                  </svg>
                </div>
                <div className="absolute bottom-0 right-0 w-[14px] h-[14px]">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M14 14V0M14 14H0" stroke="#225BD4" />
                  </svg>
                </div>
                {/* LinkedIn Button */}
                <div className="absolute top-[15px] right-[15px]">
                  <a
                    href={teamMembers[1].linkedIn}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-[40px] h-[40px] bg-[#225BD4] rounded-[5px] flex items-center justify-center hover:bg-[#1a4aa8] transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="white"
                    >
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                    </svg>
                  </a>
                </div>
              </div>

              {/* Image 3 - Bottom Left */}
              <div
                className={`relative w-full aspect-[415/500] cursor-pointer transition-opacity duration-300 group ${activeId !== 3 ? "opacity-10" : "opacity-100"}`}
                onClick={() => handleImageClick(3)}
              >
                <Image
                  src={teamMembers[2].image}
                  alt={teamMembers[2].name}
                  fill
                  className="object-cover"
                />
                {/* Corner decorations */}
                <div className="absolute top-0 left-0 w-[14px] h-[14px]">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M0 0V14M0 0H14"
                      stroke={activeId === 3 ? "#225BD4" : "white"}
                    />
                  </svg>
                </div>
                <div className="absolute bottom-0 left-0 w-[14px] h-[14px]">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M0 14V0M0 14H14"
                      stroke={activeId === 3 ? "#225BD4" : "white"}
                    />
                  </svg>
                </div>
                <div className="absolute top-0 right-0 w-[14px] h-[14px]">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M14 0V14M14 0H0"
                      stroke={activeId === 3 ? "#225BD4" : "white"}
                    />
                  </svg>
                </div>
                <div className="absolute bottom-0 right-0 w-[14px] h-[14px]">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M14 14V0M14 14H0"
                      stroke={activeId === 3 ? "#225BD4" : "white"}
                    />
                  </svg>
                </div>
                {/* LinkedIn Button */}
                <div className="absolute top-[15px] right-[15px]">
                  <a
                    href={teamMembers[2].linkedIn}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-[40px] h-[40px] bg-[#225BD4] rounded-[5px] flex items-center justify-center hover:bg-[#1a4aa8] transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="white"
                    >
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                    </svg>
                  </a>
                </div>
              </div>

              {/* Image 4 - Bottom Right (offset down) */}
              <div
                className={`relative w-full aspect-[415/500] cursor-pointer transition-opacity duration-300 group mt-[30px] lg:mt-[61px] ${activeId !== 4 ? "opacity-10" : "opacity-100"}`}
                onClick={() => handleImageClick(4)}
              >
                <Image
                  src={teamMembers[3].image}
                  alt={teamMembers[3].name}
                  fill
                  className="object-cover"
                />
                {/* Corner decorations */}
                <div className="absolute top-0 left-0 w-[14px] h-[14px]">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M0 0V14M0 0H14"
                      stroke={activeId === 4 ? "#225BD4" : "white"}
                    />
                  </svg>
                </div>
                <div className="absolute bottom-0 left-0 w-[14px] h-[14px]">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M0 14V0M0 14H14"
                      stroke={activeId === 4 ? "#225BD4" : "white"}
                    />
                  </svg>
                </div>
                <div className="absolute top-0 right-0 w-[14px] h-[14px]">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M14 0V14M14 0H0"
                      stroke={activeId === 4 ? "#225BD4" : "white"}
                    />
                  </svg>
                </div>
                <div className="absolute bottom-0 right-0 w-[14px] h-[14px]">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M14 14V0M14 14H0"
                      stroke={activeId === 4 ? "#225BD4" : "white"}
                    />
                  </svg>
                </div>
                {/* LinkedIn Button */}
                <div className="absolute top-[15px] right-[15px]">
                  <a
                    href={teamMembers[3].linkedIn}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-[40px] h-[40px] bg-[#225BD4] rounded-[5px] flex items-center justify-center hover:bg-[#1a4aa8] transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="white"
                    >
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Team Member Details */}
          <div className="w-full lg:w-[50%] flex flex-col gap-[54px]">
            {teamMembers?.map((member, index) => (
              <div
                key={member.id}
                className="flex gap-[30px] items-start cursor-pointer"
                onClick={() => handleTitleClick(member.id)}
              >
                {/* Progress Number */}
                <div className="flex flex-col items-center gap-0 w-[50px] flex-shrink-0">
                  <div
                    className={`text-[20px] clash-font leading-[120%] text-center h-[53px] flex items-center justify-center ${activeId === member.id ? "text-white" : "text-[#636364]"
                      }`}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </div>
                  {index < teamMembers.length - 1 && (
                    <div
                      className={`w-[1px] ${activeId === member.id && member.description
                        ? "h-[155px]"
                        : "h-[1px]"
                        } bg-gradient-to-b from-white/20 to-white/5`}
                    />
                  )}
                </div>

                {/* Member Info */}
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-4 mb-[15px]">
                    <h3
                      className={`text-[38px] clash-font leading-[140%] transition-all duration-300 ${activeId === member.id
                        ? "text-white font-medium"
                        : "text-white/50 font-normal"
                        }`}
                    >
                      {member.name}
                    </h3>

                    {/* Position Badge */}
                    <div className="bg-white/[0.07] backdrop-blur-[21px] border border-white/[0.09] rounded-[60px] px-[20px] py-[5px] whitespace-nowrap">
                      <span className="text-white text-[14px] leading-[140%]">
                        {member.position}
                      </span>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {activeId === member.id && member.description && (
                    <div className="flex flex-col gap-[15px] animate-fadeIn">
                      {member.quote && (
                        <p className="text-white text-[18px] font-semibold italic leading-[140%]">
                          {member.quote}
                        </p>
                      )}
                      {member.description && (
                        <p className="text-[#949596] text-[18px] leading-[140%]">
                          {member.description}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
